/**
 * Print Queue — persistent, client-side.
 *
 * When an auto-print fails (QZ Tray not running, printer offline, etc.),
 * autoPrint enqueues the order snapshot here. A background loop retries
 * quietly every 5s. The AdminOrders page shows a badge with the count.
 *
 * Everything lives in localStorage so a tab refresh doesn't lose tickets.
 */

import { getSavedPrinterName } from './qzPrinter';
import printerService, { getMode as getPrinterMode } from '../services/printer/printerService';

const STORAGE_KEY = 'qz_print_queue_v1';
const MAX_ATTEMPTS = 60;                       // fast retries → higher cap
const BASE_BACKOFF_MS = 5 * 1000;              // fixed 5s cooldown between retries
const listeners = new Set();

/**
 * Read the queue from localStorage. Never throws.
 */
export const getQueue = () => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

const writeQueue = (queue) => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(queue));
  } catch (err) {
    // localStorage may be full or blocked (private mode). Swallow — the
    // in-memory value still works for this session.
    // eslint-disable-next-line no-console
    console.warn('Print queue: failed to persist', err);
  }
  listeners.forEach((fn) => {
    try { fn(queue); } catch (err) {
      if (process.env.NODE_ENV !== 'production') console.debug('printQueue listener threw:', err);
    }
  });
};

/**
 * Subscribe to queue changes. Returns an unsubscribe fn.
 * Fires immediately with the current queue.
 */
export const subscribe = (fn) => {
  listeners.add(fn);
  try { fn(getQueue()); } catch (err) {
    if (process.env.NODE_ENV !== 'production') console.debug('printQueue subscribe callback threw:', err);
  }
  return () => listeners.delete(fn);
};

/**
 * Add (or update) an order in the queue.
 * If already queued, bumps the attempts counter + last error.
 */
export const enqueue = (order, errorMessage) => {
  if (!order || !order.id) return;
  const queue = getQueue();
  const now = Date.now();
  const idx = queue.findIndex((q) => q.orderId === order.id);
  if (idx >= 0) {
    queue[idx] = {
      ...queue[idx],
      order,                         // refresh snapshot in case fields changed
      attempts: (queue[idx].attempts || 0) + 1,
      lastAttemptAt: now,
      lastError: errorMessage || queue[idx].lastError || 'Print failed',
    };
  } else {
    queue.push({
      orderId: order.id,
      order,
      addedAt: now,
      attempts: 1,
      lastAttemptAt: now,
      lastError: errorMessage || 'Print failed',
    });
  }
  writeQueue(queue);
};

/**
 * Remove an order from the queue (successful print, or manual dismiss).
 */
export const dequeue = (orderId) => {
  const queue = getQueue().filter((q) => q.orderId !== orderId);
  writeQueue(queue);
};

/**
 * Clear the entire queue (manual "Clear all" action).
 */
export const clearQueue = () => {
  writeQueue([]);
};

/**
 * Check whether an item is eligible for the next retry pass. Uses simple
 * exponential backoff so we don't hammer QZ Tray if the printer is offline.
 */
const isDueForRetry = (item) => {
  const attempts = item.attempts || 1;
  if (attempts >= MAX_ATTEMPTS) return false;   // give up automated retries
  // Fixed 5s cooldown between retries — no exponential backoff so a
  // temporarily-offline printer prints as soon as it recovers.
  return Date.now() - (item.lastAttemptAt || 0) >= BASE_BACKOFF_MS;
};

/**
 * Try to print a single queued item. Returns true on success.
 * Callers should NOT throw — this is meant to be background-quiet.
 */
export const retryOne = async (item, printerName) => {
  // Route through printerService so the queue works for BOTH printer modes.
  // For 'network' we still require a printer name; the Android built-in
  // adapter ignores it. Failing to select a printer for 'network' silently
  // no-ops (retry will run again on the next tick).
  const mode = getPrinterMode();
  const name = printerName || getSavedPrinterName();
  if (mode === 'network' && !name) return false;

  const result = await printerService.printOrder(item.order, { printerName: name });
  if (result.success) {
    dequeue(item.orderId);
    return true;
  }

  // Update attempts/lastError but keep in queue.
  const queue = getQueue();
  const idx = queue.findIndex((q) => q.orderId === item.orderId);
  if (idx >= 0) {
    queue[idx] = {
      ...queue[idx],
      attempts: (queue[idx].attempts || 0) + 1,
      lastAttemptAt: Date.now(),
      lastError: (result.error || 'Print failed').slice(0, 200),
    };
    writeQueue(queue);
  }
  return false;
};

/**
 * Retry every item that's due. Used by both the background loop and the
 * manual "Retry all" button.
 */
export const retryDue = async (printerName) => {
  const queue = getQueue();
  if (queue.length === 0) return { attempted: 0, succeeded: 0 };
  let attempted = 0;
  let succeeded = 0;
  for (const item of queue) {
    if (!isDueForRetry(item)) continue;
    attempted += 1;
    // eslint-disable-next-line no-await-in-loop
    const ok = await retryOne(item, printerName);
    if (ok) succeeded += 1;
  }
  return { attempted, succeeded };
};

/**
 * Retry ALL queued items, ignoring the backoff. Used by the manual
 * "Retry all" button in the badge popover.
 */
export const retryAllNow = async (printerName) => {
  const queue = getQueue();
  let succeeded = 0;
  for (const item of queue) {
    // eslint-disable-next-line no-await-in-loop
    const ok = await retryOne(item, printerName);
    if (ok) succeeded += 1;
  }
  return { attempted: queue.length, succeeded };
};

/**
 * Start the background retry loop. Idempotent — safe to call from multiple
 * mounts; the interval is stored on window so it survives HMR too.
 */
let intervalId = null;
export const startBackgroundRetryLoop = (getPrinterName) => {
  if (intervalId) return; // already running
  const tick = async () => {
    const name = typeof getPrinterName === 'function'
      ? getPrinterName()
      : getSavedPrinterName();
    // In Android built-in mode there is no printer name — the retry can
    // still proceed (retryOne + the adapter handle availability checks).
    if (getPrinterMode() === 'network' && !name) return;
    if (getQueue().length === 0) return;
    try {
      await retryDue(name);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn('Print queue retry loop error:', err);
    }
  };
  intervalId = setInterval(tick, 5 * 1000);
  // Also run once on start (fires immediately).
  tick();
};

export const stopBackgroundRetryLoop = () => {
  if (intervalId) {
    clearInterval(intervalId);
    intervalId = null;
  }
};

export default {
  getQueue,
  subscribe,
  enqueue,
  dequeue,
  clearQueue,
  retryOne,
  retryDue,
  retryAllNow,
  startBackgroundRetryLoop,
  stopBackgroundRetryLoop,
};
